import {
  ExamDurationSessionType,
  type ExamDurationSettingsInput,
  type GetExamCourseSettingsByExamCourseCodeQuery,
  type UpdateExamCourseSettingsInput,
} from '@exam-center/graphql';

import { type PreExamSettingsSectionFormInputs } from '../../containers/pre-exam-settings-section-form/types';

type ExamCourseSettings =
  GetExamCourseSettingsByExamCourseCodeQuery['ecGetExamCourseByExamCourseCode']['examCourse']['settings'];

function mapExistingAttachDocumentToInput(
  attachDocument: NonNullable<ExamCourseSettings>['attachDocument'] | null | undefined,
): UpdateExamCourseSettingsInput['settings']['attachDocument'] {
  if (!attachDocument?.fileName || !attachDocument.key) {
    return null;
  }

  const uploadFileName = attachDocument.key.split('/').pop() || attachDocument.key;

  return {
    originalFileName: attachDocument.fileName,
    uploadFileName,
  };
}

export function mapExamDurationToExamCourseSettingsInput(
  examDuration: PreExamSettingsSectionFormInputs['examDuration'],
): ExamDurationSettingsInput {
  const { type, singleSessionValue, multipleSessionValue } = examDuration;

  if (type === 'single-session' && singleSessionValue) {
    return {
      type: ExamDurationSessionType.Single,
      sessions: [{ order: 1, value: singleSessionValue }],
    };
  }

  const flatMultipleSessionValue = multipleSessionValue?.flatMap((session) => session.value);
  if (
    type === 'multiple-session' &&
    flatMultipleSessionValue?.every((value) => value !== undefined)
  ) {
    return {
      type: ExamDurationSessionType.Multiple,
      sessions: flatMultipleSessionValue.map((value, order) => ({
        order: order + 1,
        value,
      })),
    };
  }

  throw new Error('Exam Duration input is not valid');
}

export function mergePreExamSettingsInput(
  existingSettings: ExamCourseSettings | null | undefined,
  newSettings: Partial<UpdateExamCourseSettingsInput['settings']>,
) {
  if (!existingSettings) {
    throw new Error('Existing settings must be provided for merging');
  }

  const existingAttachDocument = mapExistingAttachDocumentToInput(existingSettings.attachDocument);

  return {
    resultDisplay: {
      examResult: existingSettings.resultDisplay?.examResult ?? false,
    },
    resultSend: {
      portal: existingSettings.resultSend?.portal ?? false,
    },
    certificate: existingSettings.certificate
      ? {
          failedTemplate: existingSettings.certificate.failedTemplate,
          passedTemplate: existingSettings.certificate.passedTemplate,
          averageScoreCondition: existingSettings.certificate.averageScoreCondition,
          delayedDays: existingSettings.certificate.delayedDays,
          validityDurationYears: existingSettings.certificate.validityDurationYears,
        }
      : null,
    examDuration: newSettings.examDuration,
    questionnaire: {
      templateId: existingSettings.questionnaire?.templateId ?? null,
    },
    attachDocument: existingAttachDocument ?? null,
  };
}
